import React, { createContext, useContext, useState, useEffect } from 'react';

interface DebugContextType {
  debugMode: boolean;
  toggleDebugMode: () => void;
  setDebugMode: (value: boolean) => void;
  tanstackDevtools: boolean;
  setTanstackDevtools: (value: boolean) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

export const DebugProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from localStorage or default to false
  const [debugMode, setDebugModeState] = useState<boolean>(() => {
    return localStorage.getItem('debugMode') === 'true';
  });

  const [tanstackDevtools, setTanstackDevtoolsState] = useState<boolean>(() => {
    return localStorage.getItem('tanstackDevtools') !== 'false';
  });

  // Sync to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('debugMode', debugMode.toString());

    // Expose to window for easy console access
    (window as any).debugMode = debugMode;
  }, [debugMode]);

  const toggleDebugMode = () => {
    setDebugModeState(prev => !prev);
  };

  const setDebugMode = (value: boolean) => {
    setDebugModeState(value);
  };

  const setTanstackDevtools = (value: boolean) => {
    localStorage.setItem('tanstackDevtools', value.toString());
    setTanstackDevtoolsState(value);
  };

  return (
    <DebugContext.Provider value={{ debugMode, toggleDebugMode, setDebugMode, tanstackDevtools, setTanstackDevtools }}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};

// Helper function for debug logging
export const debugLog = (message: string, ...args: any[]) => {
  if ((window as any).debugMode) {
    console.log(`🔍 [DEBUG] ${message}`, ...args);
  }
};

// Helper function for debug warnings
export const debugWarn = (message: string, ...args: any[]) => {
  if ((window as any).debugMode) {
    console.warn(`⚠️ [DEBUG] ${message}`, ...args);
  }
};

// Helper function for debug errors
export const debugError = (message: string, ...args: any[]) => {
  if ((window as any).debugMode) {
    console.error(`❌ [DEBUG] ${message}`, ...args);
  }
};
